import { BlogPost } from '@/types/blog';

// Extract entities from content for AI understanding
export const extractEntities = (content: string): string[] => {
  const entities = new Set<string>();
  
  // Common AI/tech entities
  const aiEntities = ['AI', 'CodeRide', 'artificial intelligence', 'machine learning', 'LLM', 'GPT', 'Claude', 'OpenAI', 'Anthropic'];
  const techEntities = ['JavaScript', 'TypeScript', 'React', 'Node.js', 'Python', 'API', 'JSON', 'HTML', 'CSS', 'Git', 'GitHub'];
  const conceptEntities = ['context management', 'code assistant', 'developer tools', 'programming', 'software development'];
  
  const allEntities = [...aiEntities, ...techEntities, ...conceptEntities];
  
  allEntities.forEach(entity => {
    if (content.toLowerCase().includes(entity.toLowerCase())) {
      entities.add(entity);
    }
  });
  
  return Array.from(entities);
};

// Extract topics from content for AI understanding
export const extractTopics = (content: string): string[] => {
  const topics = new Set<string>();
  
  const topicKeywords = {
    'AI Development': ['ai development', 'ai coding', 'ai assistant', 'ai tools'],
    'Context Management': ['context', 'memory', 'persistence', 'state management'],
    'Developer Experience': ['developer experience', 'dx', 'productivity', 'workflow'],
    'Code Quality': ['code quality', 'best practices', 'clean code', 'refactoring'],
    'Software Engineering': ['software engineering', 'architecture', 'design patterns'],
    'Programming': ['programming', 'coding', 'development', 'software development']
  };
  
  Object.entries(topicKeywords).forEach(([topic, keywords]) => {
    if (keywords.some(keyword => content.toLowerCase().includes(keyword))) {
      topics.add(topic);
    }
  });
  
  return Array.from(topics);
};

// Calculate reading time
export const calculateReadingTime = (content: string): number => {
  const wordsPerMinute = 200;
  const wordCount = content.split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
};

// Calculate word count
export const calculateWordCount = (content: string): number => {
  return content.split(/\s+/).filter(word => word.length > 0).length;
};

// Extract mentions from content
export const extractMentions = (content: string): Array<{ "@type": string; name: string }> => {
  const mentions = [];
  
  // Common tools and companies mentioned in AI/dev content
  const tools = ['VS Code', 'Visual Studio Code', 'GitHub', 'GitLab', 'Docker', 'Kubernetes', 'AWS', 'Azure', 'Google Cloud'];
  const companies = ['OpenAI', 'Anthropic', 'Microsoft', 'Google', 'Meta', 'Apple', 'Amazon'];
  
  tools.forEach(tool => {
    if (content.includes(tool)) {
      mentions.push({ "@type": "SoftwareApplication", name: tool });
    }
  });
  
  companies.forEach(company => {
    if (content.includes(company)) {
      mentions.push({ "@type": "Organization", name: company });
    }
  });
  
  return mentions;
};

// Create Article Schema optimized for AI
export const createArticleSchema = (post: BlogPost) => ({
  "@context": "https://schema.org",
  "@type": "TechArticle",
  "@id": `https://coderide.ai/blog/${post.slug}#article`,
  "headline": post.title,
  "alternativeHeadline": post.seo?.title || undefined,
  "description": post.description,
  "image": {
    "@type": "ImageObject",
    "url": post.image || "https://coderide.ai/og-image.jpg",
    "width": 1200,
    "height": 630,
    "name": post.imageAlt || post.title,
    "caption": post.imageAlt || post.title,
    "alt": post.imageAlt || post.title
  },
  "datePublished": post.date,
  "dateModified": post.modifiedTime || post.date,
  "author": {
    "@type": "Organization",
    "name": post.author || "CodeRide Team",
    "url": "https://coderide.ai",
    "logo": {
      "@type": "ImageObject",
      "url": "https://coderide.ai/logo.svg"
    }
  },
  "publisher": {
    "@type": "Organization",
    "name": "CodeRide",
    "logo": {
      "@type": "ImageObject",
      "url": "https://coderide.ai/logo.svg",
      "width": 60,
      "height": 60
    },
    "url": "https://coderide.ai"
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `https://coderide.ai/blog/${post.slug}`
  },
  "keywords": post.tags?.join(", ") || "",
  "articleSection": (post.tags && post.tags.length > 0) ? post.tags[0] : "Technology",
  "about": post.tags?.map(tag => ({
    "@type": "Thing",
    "name": tag
  })) || [],
  "mentions": extractMentions(post.content || ""),
  "wordCount": calculateWordCount(post.content || ""),
  "timeRequired": `PT${calculateReadingTime(post.content || "")}M`,
  "inLanguage": "en-US",
  "isAccessibleForFree": true,
  "genre": "Technology",
  "audience": {
    "@type": "Audience",
    "audienceType": "Developers"
  }
});

// Create Breadcrumb Schema
export const createBreadcrumbSchema = (post: BlogPost) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://coderide.ai/"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Blog",
      "item": "https://coderide.ai/blog"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": post.title,
      "item": `https://coderide.ai/blog/${post.slug}`
    }
  ]
});

// Create Author Schema
export const createAuthorSchema = (post: BlogPost) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://coderide.ai/#organization",
  "name": post.author || "CodeRide Team",
  "url": "https://coderide.ai",
  "logo": {
    "@type": "ImageObject",
    "url": "https://coderide.ai/logo.svg"
  },
  "sameAs": [
    "https://x.com/coderide_ai"
  ],
  "description": "CodeRide makes your code transparent to AI, letting it understand your projects without you having to explain it again and again."
});

// Enhanced Organization Schema
export const createOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://coderide.ai/#organization",
  "name": "CodeRide",
  "alternateName": "CodeRide AI",
  "description": "Task Management Redesigned for AI - Persistent context management for AI coding assistants",
  "url": "https://coderide.ai",
  "applicationCategory": "DeveloperApplication",
  "operatingSystem": "Web",
  "offers": {
    "@type": "Offer",
    "availability": "https://schema.org/ComingSoon",
    "price": "0",
    "priceCurrency": "USD"
  },
  "creator": {
    "@type": "Organization",
    "name": "Pixdata"
  },
  "featureList": [
    "AI context preservation",
    "Persistent project memory", 
    "Task optimization for AI agents",
    "MCP integration"
  ],
  "screenshot": "https://coderide.ai/screenshot.jpg",
  "softwareVersion": "1.0",
  "releaseNotes": "Initial release with AI context management features"
});

// WebSite Schema with search action
export const createWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://coderide.ai/#website",
  "url": "https://coderide.ai/",
  "name": "CodeRide",
  "description": "Task Management Redesigned for AI - Persistent context management for AI coding assistants",
  "publisher": {
    "@id": "https://coderide.ai/#organization"
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://coderide.ai/blog?q={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  },
  "inLanguage": "en-US"
});

// FAQ Schema for rich snippets
export const createFAQSchema = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://coderide.ai/#faq",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is CodeRide?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "CodeRide is a tool that makes your code transparent to AI, letting it understand your projects without you having to explain it again and again."
      }
    },
    {
      "@type": "Question",
      "name": "What is \"vibe coding\" and how does CodeRide enhance it?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "\"Vibe coding\" refers to the collaborative flow of working with AI coding assistants and AI code editors. CodeRide enhances this experience by adding structure while maintaining flexibility. It transforms your casual coding sessions with AI into a disciplined, contextual workflow where your AI assistant consistently understands your project goals, constraints, and previous decisions."
      }
    },
    {
      "@type": "Question",
      "name": "How does CodeRide solve the problem of context reset between AI sessions?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Traditional AI coding assistants forget everything between chat sessions. CodeRide maintains a persistent project memory that gets passed along with each task, eliminating the need to repeatedly explain your project structure, requirements, or decisions to your AI assistant."
      }
    },
    {
      "@type": "Question",
      "name": "How does CodeRide maintain context between AI interactions?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "CodeRide creates a persistent knowledge base for your project that prevents the \"memory loss\" problem typical with AI coding assistants. It tracks decisions, architecture patterns, and implementation details across all tasks, ensuring your AI assistant maintains complete project understanding regardless of token limitations."
      }
    },
    {
      "@type": "Question",
      "name": "Which AI coding editors and IDEs does CodeRide support?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "CodeRide supports popular IDEs including VS Code with Cursor, Windsurf and VS Code with Cline or GitHub Copilot. It also integrates with AI assistants like Claude desktop through our MCP integration."
      }
    },
    {
      "@type": "Question",
      "name": "How does CodeRide integrate with AI-native code editors like Cursor, Windsurf, and Cline?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "CodeRide seamlessly connects with AI-native editors through our MCP. Your editor's AI assistant can directly access CodeRide's functions like get_project, get_task, and update_project to maintain complete context. Simply point your AI assistant to the appropriate task, and CodeRide handles all context management behind the scenes."
      }
    },
    {
      "@type": "Question",
      "name": "What programming languages does CodeRide support?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "CodeRide supports all major programming languages including JavaScript, TypeScript, Python, Java, C#, Ruby, Go, PHP, and many more. Our AI adapts to any language in your codebase."
      }
    },
    {
      "@type": "Question",
      "name": "Can I import existing task lists into CodeRide?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, CodeRide supports batch importing of tasks from documents and transforms them into AI-optimized formats. This makes it easy to continue working on existing projects while taking advantage of CodeRide's context preservation features."
      }
    }
  ]
});
